import styled from 'styled-components';
import { useListGroupRateObjectsQuery } from '@shared/api';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { fleetLeading, fleetType, typo } from '@entities/fleet';
import { PRC_COMMON_LABEL } from '@entities/prc';

const Box = styled.fieldset`
  min-width: 0;
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  padding: 0;
  border: none;

  & > legend {
    padding: 0;
    margin-bottom: ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.body};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
  }

  & > .hint {
    margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > .error {
    margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.status.danger};
  }
`;

const List = styled.div`
  display: grid;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const Option = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: start;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  font-size: ${fleetType.base};
  line-height: ${fleetLeading.normal};
  cursor: pointer;
  overflow-wrap: anywhere;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }

  & > input {
    width: 18px;
    height: 18px;
    margin: 2px 0 0;
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }

  & .code,
  & .taken {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &[data-taken='true'] {
    cursor: default;
  }
`;

/**
 * Объекты версии ПРЦ: галочки по проектам — тем же, что в шапке ОСНОВЫ.
 * Ни одной — версия общая: её цены действуют на объектах без своей ПРЦ.
 * `taken` — объекты, занятые в версии другими условиями (объект → чьи):
 * их видно, но не отметить. `hint` — своя подсказка (у варианта условий).
 */
export function ObjectsField({
  id,
  value,
  onChange,
  disabled,
  taken,
  hint,
}: {
  id: string;
  value: readonly string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  taken?: ReadonlyMap<string, string>;
  hint?: string;
}) {
  const { data, isLoading, isError } = useListGroupRateObjectsQuery();
  const objects = data?.items ?? [];
  const chosen = new Set(value);
  // Объект версии, которого нет в списке (удалён), не теряем молча.
  const missing = value.filter((v) => !objects.some((o) => o.id === v));

  function toggle(objectId: string, on: boolean) {
    const next = on ? [...value, objectId] : value.filter((v) => v !== objectId);
    onChange(Array.from(new Set(next)));
  }

  return (
    <Box id={id} disabled={disabled} aria-describedby={`${id}-hint`}>
      <legend>Объекты</legend>
      {isLoading ? (
        <div aria-busy="true" aria-label="Загрузка объектов">
          <SkeletonLine $height={18} $width="70%" />
          <SkeletonLine $height={18} $width="55%" />
        </div>
      ) : isError ? (
        <p className="error" role="alert">
          Список объектов не загрузился — расчёт сохранится общим, объекты можно задать позже.
        </p>
      ) : objects.length === 0 ? (
        <p className="hint">{typo('Объектов в ОСНОВЕ пока нет — расчёт будет общим.')}</p>
      ) : (
        <List>
          {objects.map((o) => {
            const owner = chosen.has(o.id) ? undefined : taken?.get(o.id);
            return (
              <Option key={o.id} data-taken={owner ? 'true' : undefined}>
                <input
                  type="checkbox"
                  checked={chosen.has(o.id)}
                  disabled={owner !== undefined}
                  onChange={(e) => toggle(o.id, e.target.checked)}
                />
                <span>
                  {o.name}
                  {o.code && <span className="code"> · {o.code}</span>}
                  {owner && <span className="taken"> · {typo(`уже ${owner}`)}</span>}
                </span>
              </Option>
            );
          })}
        </List>
      )}
      {missing.length > 0 && (
        <p className="hint">
          {typo(`Ещё ${missing.length} объект(а) версии нет в списке — возможно, объект удалён.`)}
        </p>
      )}
      <p className="hint" id={`${id}-hint`}>
        {typo(
          hint ??
            (value.length === 0
              ? `Ни один объект не отмечен — «${PRC_COMMON_LABEL.toLowerCase()}»: цены действуют на объектах, у которых нет своей ПРЦ.`
              : 'ПРЦ будет действовать на отмеченных объектах. Утверждение заменит действующую ПРЦ только этих объектов.'),
        )}
      </p>
    </Box>
  );
}
